import { Box, Heading } from '@zillow/constellation-10';

import {
  FieldEditModalHeaderActionProvider,
  useFieldEditModalHeaderAction,
} from '../../../../../contexts/FieldEditModalHeaderActionContext';
import { useCanAccessAIListingCreationTestB } from '../../../../../hooks/useAIListingCreation';

import { wizardHeadingStyles } from '../../../../../styles/wizard';

import RevertButton from '../../../../RevertButton';

import type { ReactNode } from 'react';

/** Matches the rendered height of RevertButton, so showing it cannot shift the section. */
const REVERT_BUTTON_HEIGHT_PX = '40px';

type Props = {
  title: string;
  headingTextStyle: 'heading-xs' | 'heading-sm';
  /** For sections whose field sits straight under the heading, such as the description textarea. */
  hasRowSpacing?: boolean;
  children: ReactNode;
};

const SectionHeading = ({ title, headingTextStyle, hasRowSpacing }: Omit<Props, 'children'>) => {
  const { headerAction } = useFieldEditModalHeaderAction();
  const isAiListingFlow = useCanAccessAIListingCreationTestB();

  return (
    // The row reserves the Revert button's height so the section does not jump when the button
    // appears. The heading keeps the spacing it has everywhere else.
    <Box
      css={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 'default',
        minHeight: isAiListingFlow ? REVERT_BUTTON_HEIGHT_PX : undefined,
        marginBottom: isAiListingFlow && hasRowSpacing ? '200' : undefined,
      }}
    >
      <Heading level={2} textStyle={headingTextStyle} css={wizardHeadingStyles}>
        {title}
      </Heading>
      {headerAction && <RevertButton ariaLabel={headerAction.ariaLabel} onRevert={headerAction.onAction} />}
    </Box>
  );
};

/** Hosts a field's revert action next to the section heading, the way the field edit modal hosts it in its header. */
const AiRevertSection = ({ title, headingTextStyle, hasRowSpacing, children }: Props) => (
  <FieldEditModalHeaderActionProvider isInFieldEditModal={false}>
    <SectionHeading title={title} headingTextStyle={headingTextStyle} hasRowSpacing={hasRowSpacing} />
    {children}
  </FieldEditModalHeaderActionProvider>
);

export default AiRevertSection;
